import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type {
  SurveyAssumptions,
  EquipmentPortCountAssumption,
  AssumptionsFileData,
} from '@/types/assumptions.types'
import * as UnifiedStorage from '@/services/unified-storage.service'
import { useStorageAdapter, type StoreOperationResult } from '@/composables/useStorageAdapter'
import { StorageErrorCode } from '@/services/storage/IStorageAdapter'
import type { IStorageAdapter } from '@/services/storage/IStorageAdapter'
import { reportError } from '@/lib/error-reporting'

/**
 * Store for managing survey assumptions
 * Allows users to override equipment properties for analysis purposes
 * Uses unified storage service for automatic persistence (fallback)
 * Supports IStorageAdapter for SQL storage (when injected)
 */
export const useAssumptionsStore = defineStore('assumptions', () => {
  // Storage adapter composable
  const storage = useStorageAdapter<SurveyAssumptions>()

  // Load from unified storage on initialization (async)
  const loadFromStorage = async (): Promise<Map<string, SurveyAssumptions>> => {
    try {
      const allSurveys = await UnifiedStorage.getAllSurveys()
      const map = new Map<string, SurveyAssumptions>()

      Object.values(allSurveys).forEach(survey => {
        map.set(survey.surveyId, survey.assumptions)
      })

      return map
    } catch (error) {
      reportError(error, { where: 'assumptions.store.loadFromStorage' })
      return new Map()
    }
  }

  // Save to unified storage (async)
  const saveToStorage = async (assumptionsMap: Map<string, SurveyAssumptions>): Promise<void> => {
    try {
      for (const [surveyId, assumptions] of assumptionsMap.entries()) {
        const updated = await UnifiedStorage.updateSurveyAssumptions(surveyId, assumptions)
        // Update local data with new version from server
        assumptionsMap.set(surveyId, updated)
      }
    } catch (error) {
      reportError(error, { where: 'assumptions.store.saveToStorage' })
    }
  }

  // State
  const assumptions = ref<Map<string, SurveyAssumptions>>(new Map())
  const activeSurveyId = ref<string | null>(UnifiedStorage.getActiveSurveyId())
  const isInitialized = ref(false)
  const isViewingSnapshot = ref(false) // Track when viewing historical version data

  // Initialize async. Capture the promise so actions can await readiness before
  // touching the Map — otherwise an early caller mutates the empty initial Map
  // and the `.then()` below wholesale-replaces it, clobbering saved data (#452).
  const initPromise = loadFromStorage().then(loadedAssumptions => {
    assumptions.value = loadedAssumptions
    isInitialized.value = true
  })

  /** Resolves once the initial load from storage has populated the Map. */
  function whenReady(): Promise<void> {
    return initPromise
  }

  // Getters
  const activeAssumptions = computed(() => {
    if (!activeSurveyId.value) return null
    return assumptions.value.get(activeSurveyId.value) || null
  })

  const hasAssumptions = computed(() => {
    return activeSurveyId.value && assumptions.value.has(activeSurveyId.value)
  })

  const assumptionCount = computed(() => {
    if (!activeAssumptions.value) return 0
    return Object.keys(activeAssumptions.value.equipmentPortCounts).length
  })

  // Actions

  /**
   * Set storage adapter for SQL storage support
   */
  function setStorageAdapter(adapter: IStorageAdapter<SurveyAssumptions>): void {
    storage.setAdapter(adapter)
  }

  /**
   * Load assumptions from storage adapter
   */
  async function loadAssumptions(surveyId: string): Promise<boolean> {
    const adapter = storage.adapter()
    if (!adapter) {
      return true // No adapter, use fallback
    }

    const result = await storage.load(surveyId)

    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('[AssumptionsStore] Load result:', {
        success: result.success,
        hasData: result.success ? !!result.data : false,
        hasDataData: result.success ? !!(result.data as any)?.data : false,
        resultData: result.success ? result.data : null,
        fullResult: result,
      })
    }

    if (!result.success) {
      return false
    }

    // Check if we got data back from SQL storage
    if (result.data && 'data' in result.data && result.data.data) {
      // Merge version from metadata back into the data object
      const loadedData = {
        ...(result.data.data as SurveyAssumptions),
        version: result.data.metadata.version,
      }
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log('[AssumptionsStore] ✅ Setting loaded data:', loadedData)
      }
      assumptions.value.set(surveyId, loadedData)
    } else {
      // Initialize empty assumptions structure if none exists (404 or empty response)
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log('[AssumptionsStore] No data found in storage, initializing empty')
      }
      const emptyAssumptions: SurveyAssumptions = {
        surveyId,
        surveyName: surveyId, // Fallback - will be updated in setActiveSurvey
        equipmentPortCounts: {},
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        version: 1,
      }
      assumptions.value.set(surveyId, emptyAssumptions)
    }

    return true
  }

  /**
   * Get version for a survey (for optimistic locking)
   */
  function getVersion(surveyId: string): number | undefined {
    return storage.getVersion(surveyId)
  }

  /**
   * Register conflict handler
   */
  function onConflict(handler: (_conflict: unknown) => void): () => void {
    return storage.onConflict(handler)
  }

  /**
   * Register auth error handler
   */
  function onAuthError(handler: () => void): () => void {
    return storage.onAuthError(handler)
  }

  async function setActiveSurvey(surveyId: string, surveyName: string): Promise<void> {
    // Wait for the initial load to populate the Map before reading/mutating it,
    // so we never operate on the transient empty Map and clobber saved data (#452).
    await whenReady()

    activeSurveyId.value = surveyId

    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('[AssumptionsStore] setActiveSurvey called for:', surveyId)
    }

    // If viewing snapshot data, skip loading from storage to preserve restored state
    if (isViewingSnapshot.value && assumptions.value.has(surveyId)) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log('[AssumptionsStore] Viewing snapshot, skipping storage load')
      }
      return
    }

    // If we have a SQL adapter, use it directly
    const adapter = storage.adapter()

    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('[AssumptionsStore] Has adapter:', !!adapter)
    }

    if (adapter) {
      // Try to load from SQL storage adapter
      const loaded = await loadAssumptions(surveyId)

      // Ensure the survey is in the map even if load failed
      if (!loaded || !assumptions.value.has(surveyId)) {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.log('[AssumptionsStore] Initializing empty assumptions for:', surveyId)
        }
        const emptyAssumptions: SurveyAssumptions = {
          surveyId,
          surveyName: surveyName,
          equipmentPortCounts: {},
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          version: 1,
        }
        assumptions.value.set(surveyId, emptyAssumptions)
      }

      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log('[AssumptionsStore] Survey active, has data:', assumptions.value.has(surveyId))
      }
      return
    }

    // No SQL adapter - fall back to unified storage (localStorage)
    const surveyData = await UnifiedStorage.initializeSurvey({ surveyId, surveyName })
    if (!assumptions.value.has(surveyId)) {
      assumptions.value.set(surveyId, surveyData.assumptions)
    }
  }

  async function setEquipmentPortCount(
    assumption: EquipmentPortCountAssumption
  ): Promise<StoreOperationResult<SurveyAssumptions>> {
    if (!activeSurveyId.value) {
      if (import.meta.env.DEV) {
        console.error('[AssumptionsStore] No active survey ID')
      }
      return {
        success: false,
        error: {
          code: StorageErrorCode.UNKNOWN,
          message: 'No active survey',
        },
      }
    }

    const surveyAssumptions = assumptions.value.get(activeSurveyId.value)
    if (!surveyAssumptions) {
      if (import.meta.env.DEV) {
        console.error('[AssumptionsStore] Survey assumptions not found for:', activeSurveyId.value)
        // eslint-disable-next-line no-console
        console.log('[AssumptionsStore] Available surveys:', Array.from(assumptions.value.keys()))
      }
      return {
        success: false,
        error: {
          code: StorageErrorCode.UNKNOWN,
          message: 'Survey assumptions not found',
        },
      }
    }

    // Save previous state for rollback
    const hadAssumption = assumption.equipmentDefinitionId in surveyAssumptions.equipmentPortCounts
    const previousAssumption = hadAssumption
      ? surveyAssumptions.equipmentPortCounts[assumption.equipmentDefinitionId]
      : undefined
    const previousUpdatedAt = surveyAssumptions.updatedAt

    // Optimistic update
    surveyAssumptions.equipmentPortCounts[assumption.equipmentDefinitionId] = {
      ...assumption,
      modifiedAt: new Date().toISOString(),
    }
    surveyAssumptions.updatedAt = new Date().toISOString()

    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('[AssumptionsStore] Saving assumption for:', assumption.equipmentName)
      // eslint-disable-next-line no-console
      console.log('[AssumptionsStore] Has adapter:', !!storage.adapter())
    }

    // If storage adapter is set, use it
    if (storage.adapter()) {
      const result = await storage.save(activeSurveyId.value, surveyAssumptions)

      if (!result.success) {
        // Rollback on failure
        if (hadAssumption && previousAssumption) {
          surveyAssumptions.equipmentPortCounts[assumption.equipmentDefinitionId] =
            previousAssumption
        } else {
          delete surveyAssumptions.equipmentPortCounts[assumption.equipmentDefinitionId]
        }
        surveyAssumptions.updatedAt = previousUpdatedAt
        return result
      }

      return result
    }

    // Fallback to unified storage
    saveToStorage(assumptions.value)
    return { success: true }
  }

  async function removeEquipmentPortCount(equipmentDefinitionId: string): Promise<void> {
    if (!activeSurveyId.value) return

    const surveyAssumptions = assumptions.value.get(activeSurveyId.value)
    if (!surveyAssumptions) return

    delete surveyAssumptions.equipmentPortCounts[equipmentDefinitionId]
    surveyAssumptions.updatedAt = new Date().toISOString()
    await saveToStorage(assumptions.value)
  }

  /**
   * Clear all assumptions for a survey
   * @param surveyId - Survey ID to clear (defaults to active survey)
   * @param skipPersist - If true, only clears local state without persisting (used when data already deleted from SQL)
   */
  async function clearAssumptions(surveyId?: string, skipPersist = false): Promise<void> {
    const targetId = surveyId || activeSurveyId.value
    if (!targetId) return

    const surveyAssumptions = assumptions.value.get(targetId)
    if (surveyAssumptions) {
      surveyAssumptions.equipmentPortCounts = {}
      surveyAssumptions.updatedAt = new Date().toISOString()
      if (!skipPersist) {
        await saveToStorage(assumptions.value)
      }
    }
  }

  function getEquipmentPortCount(equipmentDefinitionId: string): number | null {
    if (!activeSurveyId.value) return null

    const surveyAssumptions = assumptions.value.get(activeSurveyId.value)
    if (!surveyAssumptions) return null

    const assumption = surveyAssumptions.equipmentPortCounts[equipmentDefinitionId]
    return assumption ? assumption.assumedPortCount : null
  }

  /**
   * Export assumptions to JSON file
   */
  function exportAssumptions(surveyId?: string): AssumptionsFileData | null {
    const targetId = surveyId || activeSurveyId.value
    if (!targetId) return null

    const surveyAssumptions = assumptions.value.get(targetId)
    if (!surveyAssumptions) return null

    return {
      fileVersion: '1.0',
      exportedAt: new Date().toISOString(),
      assumptions: surveyAssumptions,
    }
  }

  /**
   * Import assumptions from JSON file
   */
  async function importAssumptions(data: AssumptionsFileData): Promise<boolean> {
    try {
      const { assumptions: importedAssumptions } = data

      // Validate the data
      if (!importedAssumptions.surveyId || !importedAssumptions.equipmentPortCounts) {
        return false
      }

      // Set as active survey
      activeSurveyId.value = importedAssumptions.surveyId

      // Update the assumptions
      assumptions.value.set(importedAssumptions.surveyId, {
        ...importedAssumptions,
        updatedAt: new Date().toISOString(),
      })

      // Save to storage
      await saveToStorage(assumptions.value)

      return true
    } catch (error) {
      reportError(error, { where: 'assumptions.store.importAssumptions' })
      return false
    }
  }

  /**
   * Download assumptions as JSON file
   */
  function downloadAssumptionsFile(surveyId?: string) {
    const data = exportAssumptions(surveyId)
    if (!data) return

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `survey-assumptions-${data.assumptions.surveyId}-${Date.now()}.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  /**
   * Get the complete state for a survey (for report snapshots)
   */
  function getStateForSurvey(surveyId: string): SurveyAssumptions | null {
    return assumptions.value.get(surveyId) || null
  }

  /**
   * Restore state for a survey from a snapshot
   * @param surveyId - The survey ID to restore state for
   * @param state - The state to restore
   * @param skipPersist - If true, only update in-memory state (for version switching)
   */
  async function restoreStateForSurvey(
    surveyId: string,
    state: SurveyAssumptions,
    skipPersist = false
  ): Promise<void> {
    if (!state || state.surveyId !== surveyId) {
      return
    }

    // Mark as viewing snapshot if skipping persist (i.e., loading historical version)
    if (skipPersist) {
      isViewingSnapshot.value = true
    }

    // Update in-memory state only
    assumptions.value.set(surveyId, state)

    // Only persist if not restoring from snapshot (version switching)
    if (!skipPersist) {
      await saveToStorage(assumptions.value)
    }
  }

  /**
   * Clear the snapshot viewing flag.
   * Call this when generating a new report or switching to the latest version
   * to allow loading from storage again.
   */
  function clearSnapshotMode(): void {
    isViewingSnapshot.value = false
  }

  return {
    // State
    assumptions,
    activeSurveyId,
    isViewingSnapshot,
    isInitialized,
    whenReady,

    // Getters
    activeAssumptions,
    hasAssumptions,
    assumptionCount,

    // Actions
    setStorageAdapter,
    loadAssumptions,
    getVersion,
    onConflict,
    onAuthError,
    lastError: storage.lastError,
    setActiveSurvey,
    setEquipmentPortCount,
    removeEquipmentPortCount,
    clearAssumptions,
    getEquipmentPortCount,
    exportAssumptions,
    importAssumptions,
    downloadAssumptionsFile,
    getStateForSurvey,
    restoreStateForSurvey,
    clearSnapshotMode,
  }
})
